import { useEffect, useState } from 'react';
import { read_cookie } from "sfcookies";
import { hostUrl } from "../../../../utils/url-manager";
import { AllowancePool } from "./customer-account-allowances";
import { SeatChange, SeatInvitation, SeatPerson } from "../seat-panel-format";

export type AccountSeats = {
    customerAccountId: number,
    isSubscribed: boolean,
    seats: AllowancePool,
    /** The domains this account has proved it controls. Only these can hold seats. */
    verifiedDomains: string[],
    /** Holds a seat, and is not in `seatHolders`. */
    owner: SeatPerson|null,
    seatHolders: SeatPerson[],
    /**
     * Customers not already on the account, the ones on a verified domain first.
     *
     * Capped by the API, so the search term is what narrows it.
     */
    candidates: SeatPerson[],
    /** Sent and not yet accepted. These hold no seat until they are. */
    invitations: SeatInvitation[],
};

type UseAccountSeats = {
    loadingSeats: boolean,
    seatsError: string,
    seats: AccountSeats|undefined,
    reloadSeats: (search?: string) => Promise<void>,
};

/**
 * Everything the Seats tab shows, in one request.
 *
 * One request rather than four because every part of the panel is a view of the same
 * account, and a panel assembled from four separately-timed answers shows numbers that
 * disagree with each other while it settles.
 */
export const useAccountSeats: (customerAccountId: string|number|undefined) => UseAccountSeats = (
    customerAccountId: string|number|undefined
): UseAccountSeats => {
    const [loadingSeats, setLoadingSeats] = useState<boolean>(true);
    const [seatsError, setSeatsError] = useState<string>('');
    const [seats, setSeats] = useState<AccountSeats|undefined>();

    async function reloadSeats(search: string = ''): Promise<void> {
        if (customerAccountId === undefined || customerAccountId === '') {
            return;
        }
        setLoadingSeats(true);
        // The candidate list is every customer in the database, capped by the API, so the
        // search is what narrows it rather than a filter over an already-complete list.
        const url: string = hostUrl + 'hq/customer/account/seats?customerAccountId=' + customerAccountId
            + (search.trim() === '' ? '' : '&search=' + encodeURIComponent(search.trim()));
        const token: string = read_cookie('auth');
        const request: Request = new Request(url, {
            method: 'GET',
            headers: new Headers({
                'Content-Type': 'application/json',
                'token': token,
            })
        });

        return fetch(request)
            .then((response: Response): Promise<any> => response.json())
            .then((data: any): void => {
                setLoadingSeats(false);
                if (data?.status === 'success') {
                    setSeatsError('');
                    setSeats(data?.data);
                    return;
                }
                setSeatsError(data?.message ?? 'Could not read this account\'s seats.');
            })
            .catch((error: Error): void => {
                setLoadingSeats(false);
                setSeatsError('Could not read this account\'s seats.');
                console.error(error);
            });
    }

    useEffect((): void => {
        reloadSeats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerAccountId]);

    return { loadingSeats, seatsError, seats, reloadSeats };
}

export type SeatChangeReport = {
    status: 'success'|'error',
    message: string,
    /** The Seats tab as it stands after the change, on success. */
    data?: AccountSeats,
};

/**
 * Give one customer a seat, or take one away, straight away (HQAPI-92).
 *
 * The Seats tab used to edit the account form's `seatHolders` list and wait for Save. HQ-API now
 * writes each change as it is made, refusing a grant when the subscription has no seat left, so
 * the tab and the Subscription tab can never disagree about the limit.
 *
 * Always resolves with a report the panel can show, whatever happened to the request.
 */
export async function changeSeat(
    change: SeatChange,
    customerAccountId: string|number,
    customerId: string|number
): Promise<SeatChangeReport> {
    const failure: string = 'That seat could not be changed.';

    try {
        const response: Response = await fetch(new Request(hostUrl + 'hq/customer/account/seats/' + change, {
            method: 'POST',
            body: JSON.stringify({ customerAccountId: customerAccountId, customerId: customerId }),
            headers: new Headers({
                'Content-Type': 'application/json',
                'token': read_cookie('auth'),
                'hq': 'hq',
            }),
        }));

        // A role without the edit ability gets Symfony's access denied page, not JSON.
        if (response.status === 403) {
            return { status: 'error', message: 'You do not have permission to change seats on this account.' };
        }

        const body: any = await response.json();
        if (body?.status === 'success') {
            return { status: 'success', message: body?.message ?? 'Seat changed', data: body?.data };
        }

        return { status: 'error', message: body?.message ?? failure };
    } catch (error: unknown) {
        console.error(error);

        return { status: 'error', message: failure };
    }
}
